import { MonthCount } from '../../types/audit';
import { formatCount, monthLong, monthName, monthShort, monthYear } from './businessStatusFormat';

interface SparklineProps {
  /** Oldest first; the last entry is the current, partial month (drawn dashed with a hollow dot). */
  monthly: MonthCount[];
  /** "New garages per month". */
  caption: string;
}

const W = 100;
const H = 40;
const PAD = 5;

const pt = (p: [number, number]) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`;

/** "New garages per month, May to October 2026: May 1, Jun 2, … Oct 3. October is month to date." */
export function sparklineLabel(caption: string, monthly: MonthCount[]): string {
  if (monthly.length === 0) return `${caption}: no data`;
  const first = monthly[0].month;
  const last = monthly[monthly.length - 1].month;
  const from = monthYear(first) === monthYear(last) ? monthName(first) : monthLong(first);
  const series = monthly.map((m) => `${monthShort(m.month)} ${formatCount(m.count)}`).join(', ');
  return `${caption}, ${from} to ${monthLong(last)}: ${series}. ${monthName(last)} is month to date.`;
}

/**
 * Spec 070 — 6-month sparkline of NEW items per month, inline SVG. All month labels are visible and the
 * full series is in the aria-label, so hover (an SVG <title> per month column) is never needed to read it.
 */
export default function Sparkline({ monthly, caption }: SparklineProps) {
  const v = monthly.map((m) => Math.max(0, m.count || 0));
  const n = v.length;
  const label = sparklineLabel(caption, monthly);
  const lastFull = n >= 2 ? monthly[n - 2] : null;

  let plot = null;
  if (n >= 2) {
    const max = Math.max(...v);
    const min = Math.min(...v);
    const span = max - min || 1;
    const flat = max === min;
    const pts: [number, number][] = v.map((y, i) => [
      (i / (n - 1)) * W,
      flat ? H - PAD : H - PAD - ((y - min) / span) * (H - PAD * 2),
    ]);
    const solid = pts.slice(0, n - 1);
    const area = `M0,${H} L${solid.map(pt).join(' L')} L${pts[n - 2][0].toFixed(2)},${H} Z`;

    plot = (
      <div className="bs-spark__plot">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
          <path className="bs-spark__area" d={area} />
          {solid.length >= 2 && <polyline className="bs-spark__line" points={solid.map(pt).join(' ')} />}
          <polyline className="bs-spark__partial" points={pts.slice(n - 2).map(pt).join(' ')} data-testid="spark-partial" />
          {pts.map((p, i) => {
            const x0 = i === 0 ? 0 : (pts[i - 1][0] + p[0]) / 2;
            const x1 = i === n - 1 ? W : (pts[i + 1][0] + p[0]) / 2;
            return (
              <rect key={monthly[i].month} className="bs-spark__hit" x={x0.toFixed(2)} y="0" width={(x1 - x0).toFixed(2)} height={H}>
                <title>{`${monthLong(monthly[i].month)}${i === n - 1 ? ' (month to date)' : ''}: ${formatCount(v[i])}`}</title>
              </rect>
            );
          })}
        </svg>
        <div className="bs-dots" aria-hidden="true">
          {pts.map((p, i) => (
            <span
              key={monthly[i].month}
              className={`bs-dot${i === n - 1 ? ' bs-dot--partial' : ''}${i === n - 2 ? ' bs-dot--last' : ''}`}
              style={{ left: `${p[0]}%`, top: `${p[1]}px` }}
            />
          ))}
        </div>
      </div>
    );
  } else {
    plot = <div className="bs-spark__plot" role="img" aria-label={label} />;
  }

  return (
    <figure className="bs-spark">
      <figcaption className="bs-spark__cap">
        <span>{caption}</span>
        {lastFull && <span>{monthShort(lastFull.month)} <b>{formatCount(lastFull.count)}</b></span>}
      </figcaption>
      {plot}
      <div className="bs-spark__months" aria-hidden="true">
        {monthly.map((m, i) => (
          <span key={m.month} title={i === n - 1 ? 'Month to date' : undefined}>
            {monthShort(m.month)}{i === n - 1 ? '*' : ''}
          </span>
        ))}
      </div>
    </figure>
  );
}
